/**
 * Authentication Actions
 * 
 * JWT-based authentication with dbutler_backend API
 * NO BACKWARD COMPATIBILITY - Clean break from Basic Auth
 */

import * as actionTypes from "./actionTypes";
import apiClient from "../../services/apiClient";
import { API_ENDPOINTS } from "../../config/api.config";
import {
  saveTokens,
  saveUser,
  getUser,
  clearTokens,
  isAuthenticated,
} from "../../utils/tokenManager";
import { showErrorToast, showSuccessToast } from "../../utils/errorHandler";
import { getProfileDetailsSuccess } from "./profile";
import { getMerchantProfile } from "./profile";

// ==============================
// ACTION CREATORS
// ==============================

export const authLoginStart = () => ({
  type: actionTypes.AUTH_LOGIN_START,
});

export const authLoginSuccess = (user) => ({
  type: actionTypes.AUTH_LOGIN_SUCCESS,
  profileData: user,
});

export const authLoginFail = (message) => ({
  type: actionTypes.AUTH_LOGIN_FAIL,
  message,
});

export const authCheckStart = () => ({
  type: actionTypes.AUTH_CHECK_START,
});

export const authCheckSuccess = () => ({
  type: actionTypes.AUTH_CHECK_SUCCESS,
});

export const authCheckFail = () => ({
  type: actionTypes.AUTH_CHECK_FAIL,
});

export const authLogoutAction = () => ({
  type: actionTypes.AUTH_LOGOUT,
});

export const authResetPasswordStart = () => ({
  type: actionTypes.AUTH_RESET_PASSWORD_START,
});

export const authResetPasswordSuccess = () => ({
  type: actionTypes.AUTH_RESET_PASSWORD_SUCCESS,
});

export const authResetPasswordFail = () => ({
  type: actionTypes.AUTH_RESET_PASSWORD_FAIL,
});

export const authSendNewPassStart = () => ({
  type: actionTypes.AUTH_SEND_NEW_PASS_START,
});

export const authSendNewPassSuccess = () => ({
  type: actionTypes.AUTH_SEND_NEW_PASS_SUCCESS,
});

export const authSendNewPassFail = () => ({
  type: actionTypes.AUTH_SEND_NEW_PASS_FAIL,
});

export const changeScreenValue = (value) => ({
  type: actionTypes.AUTH_SCREEN_CHANGE,
  value,
});

// ==============================
// AUTHENTICATION OPERATIONS
// ==============================

/**
 * Login with email and password
 * Uses JWT authentication - stores tokens, not credentials
 */
export const login = (email, password, navigate) => {
  return async (dispatch) => {
    dispatch(authLoginStart());

    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH_LOGIN, {
        email,
        password,
      });

      // Backend wraps response in { success, data, message }
      const { user, accessToken, refreshToken } = response.data.data;

      // Verify user has merchant role
      if (user.role !== 'MERCHANT_MANAGER' && user.role !== 'MERCHANT_STAFF') {
        dispatch(authLoginFail('Access denied. Merchant credentials required.'));
        showErrorToast({ message: 'Access denied. This portal is for merchant users only.' });
        return;
      }

      // Save tokens and user data
      saveTokens({ accessToken, refreshToken });
      saveUser(user);

      // Update Redux state
      dispatch(authLoginSuccess(user));
      dispatch(authCheckSuccess());
      
      // Fetch full merchant profile (includes logo) before navigating
      // This prevents avatar blinking by ensuring all profile data is loaded
      await dispatch(getMerchantProfile(user.merchantId));

      // Navigate to home page
      if (navigate) {
        navigate('/');
      }

    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Login failed. Please check your credentials.';
      
      dispatch(authLoginFail(errorMessage));
      showErrorToast({ message: errorMessage });
    }
  };
};

/**
 * Check authentication status on app load
 * Verifies JWT token and loads user data
 */
export const checkAuthStatus = () => {
  return async (dispatch) => {
    dispatch(authCheckStart());

    // Check if tokens exist
    if (!isAuthenticated()) {
      dispatch(authCheckFail());
      return;
    }

    try {
      // Verify token with backend and get current user
      const response = await apiClient.get(API_ENDPOINTS.AUTH_ME);
      
      // Backend wraps response in { success, data: { user }, message }
      const { user } = response.data.data;

      // Verify user has merchant role
      if (user.role !== 'MERCHANT_MANAGER' && user.role !== 'MERCHANT_STAFF') {
        clearTokens();
        dispatch(authCheckFail());
        return;
      }

      // Update stored user data
      saveUser(user);

      // Update Redux state
      dispatch(authLoginSuccess(user));
      dispatch(authCheckSuccess());
      
      // Fetch full merchant profile (includes logo)
      // This ensures profile data is available on app reload
      await dispatch(getMerchantProfile(user.merchantId));

    } catch (error) {
      // Token is invalid or expired
      clearTokens();
      dispatch(authCheckFail());
    }
  };
};

/**
 * Logout user
 * Calls backend logout endpoint and clears local tokens
 */
export const logout = () => {
  return async (dispatch) => {
    try {
      // Call backend logout endpoint
      await apiClient.post(API_ENDPOINTS.AUTH_LOGOUT);
    } catch (error) {
      // Continue with logout even if backend call fails
      console.error('Logout API call failed:', error);
    }

    // Clear tokens and user data
    clearTokens();

    // Update Redux state
    dispatch(authLogoutAction());
  };
};

/**
 * Request password reset code
 */
export const forgotPassword = (email) => {
  return async (dispatch) => {
    dispatch(authResetPasswordStart());

    try {
      await apiClient.post(API_ENDPOINTS.AUTH_FORGOT_PASSWORD, { email });

      dispatch(authResetPasswordSuccess());
      showSuccessToast('Password reset code sent to your email!');

    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to send reset code.';
      
      dispatch(authResetPasswordFail());
      showErrorToast({ message: errorMessage });
    }
  };
};

/**
 * Reset password with code
 */
export const sendNewPass = (code, password) => {
  return async (dispatch) => {
    dispatch(authSendNewPassStart());

    try {
      await apiClient.post(API_ENDPOINTS.AUTH_RESET_PASSWORD, {
        code,
        password,
      });

      dispatch(authSendNewPassSuccess());
      showSuccessToast('Password reset successfully! Please login with your new password.');

    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to reset password.';
      
      dispatch(authSendNewPassFail());
      showErrorToast({ message: errorMessage });
    }
  };
};

/**
 * Change authentication screen (login/forgot password)
 */
export const changeScreen = (screen) => {
  return (dispatch) => {
    dispatch(changeScreenValue(screen));
  };
};
